import { useCallback } from 'react';
import { useMutation, UseMutationConfig } from 'react-relay';
import type { Disposable, GraphQLTaggedNode, MutationParameters } from 'relay-runtime';
import { InsertUserAction } from '../graphQLOperations/UserAction';
import type {
  UserActionInput,
  UserActionMutation,
} from '../graphQLOperations/__generated__/UserActionMutation.graphql';
import {
  getUserActionPayloadErrors,
  hasUserActionNode,
  reportUserActionMissingNode,
  reportUserActionPayloadFailure,
  reportUserActionTransportFailure,
  UserActionMutationResponse,
} from '../utils/userActionSentry';

/**
 * Both user-action mutations (`UserActionMutation` and `SkillResourceCreateTimelineEventMutation`)
 * satisfy this, so one hook serves both. The input stays the generated `UserActionInput` rather than
 * a hand-written subset, so call-site object literals still get their full excess-property check.
 */
export type UserActionMutationLike = MutationParameters & {
  readonly response: UserActionMutationResponse;
  readonly variables: { input: { userAction: UserActionInput } };
};

/**
 * `useMutation` for user-action writes, with the failure check the plain hook cannot give you.
 *
 * A failed user-action write is otherwise invisible: Relay splits failures across two callbacks that
 * never both fire — `onError` for transport failures, `onCompleted` for the mutation-payload errors
 * the backend returns under HTTP 200 — and no call site read either. This wrapper reports both to
 * Sentry and then delegates to whatever the caller passed, so existing handlers keep working.
 *
 * Observation only: it does not retry, swallow, or otherwise change what the caller sees.
 */
export const useUserActionMutation = <TMutation extends UserActionMutationLike = UserActionMutation>(
  mutation: GraphQLTaggedNode = InsertUserAction,
): [(config: UseMutationConfig<TMutation>) => Disposable, boolean] => {
  const [commit, isInFlight] = useMutation<TMutation>(mutation);

  const commitUserAction = useCallback(
    (config: UseMutationConfig<TMutation>) => {
      const { actionTypeId, description, skillId } = config.variables.input.userAction;
      const fields = {
        operationName: typeof mutation === 'object' && 'params' in mutation ? mutation.params.name : 'UserActionWrite',
        actionTypeId,
        description,
        ...(skillId && { skillId }),
      };

      return commit({
        ...config,
        onCompleted: (response, errors) => {
          const payloadErrors = getUserActionPayloadErrors(response);

          if (payloadErrors.length > 0) {
            reportUserActionPayloadFailure(payloadErrors, fields);
          } else if (!hasUserActionNode(response)) {
            reportUserActionMissingNode(fields);
          }

          config.onCompleted?.(response, errors);
        },
        onError: error => {
          reportUserActionTransportFailure(error, fields);
          config.onError?.(error);
        },
      });
    },
    [commit, mutation],
  );

  return [commitUserAction, isInFlight];
};
